import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';

import { TenantDetailDailogComponent } from '@components/tenant-list/tenant-detail-dailog/tenant-detail-dailog.component';
import { TenantDetailDialogViewModel } from '@components/tenant-list/tenant-detail-dailog/tenant-detail-dailog.model';

import { firstValueFrom } from 'rxjs';
import { TenantService } from '@services/api/tenant.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FilterService } from '@services/filter.service';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';

/** マクニカ管理者向けテーブルカラム */
export const TENANT_TABLE_COLUMN_LIST: string[] = [
  'no',
  'tenantId',
  'tenantName',
  'companyName',
  'description',
  'operation',
];

/** テナントのViewModel */
export type TenantViewModel = {
  /** テナントID */
  tenantId: string;
  /** テナント名 */
  tenantName: string;
  /** 説明 */
  description: string;
  /** 会社名 */
  companyName: string;
  /** 会社ロゴのパス */
  companyLogoPath?: string;
};

interface TenantListFilter {
  tenantId?: string;
  tenantName?: string;
  companyName?: string;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'tenantList';

@Component({
  selector: 'app-tenant-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DirectivesModule,
  ],
  templateUrl: './tenant-list-table.component.html',
  styleUrl: './tenant-list-table.component.scss',
})
export class TenantListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** 新規登録を通知 */
  @Output() created = new EventEmitter<void>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<TenantViewModel>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<string>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** テナント一覧 */
  @Input({ required: true }) public tenantList: TenantViewModel[] = [];

  /** カラム */
  public displayedColumns: string[] = TENANT_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<TenantViewModel>([]);

  /** テナントID検索キーワード */
  public tenantIdKeyword = '';

  /** テナント名検索キーワード */
  public tenantNameKeyword = '';

  /** 会社名検索キーワード */
  public companyNameKeyword = '';

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'tenantId',
    direction: 'asc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private tenantService: TenantService,
    private dialog: MatDialog,
    private filterService: FilterService<TenantListFilter, TenantViewModel>,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public ngOnInit(): void {
    this.dataSource.data = this.tenantList;
    this.dataSource.sort = this.sort;

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルタ設定
    this.dataSource.filterPredicate = (element: TenantViewModel) => {
      let tenantId = true;
      if (this.tenantIdKeyword) {
        // tenantIdKeywordが空でない場合のみフィルタリングを行う
        tenantId =
          element.tenantId
            .trim()
            .toLowerCase()
            .indexOf(this.tenantIdKeyword.toLowerCase()) > -1;
      }
      let tenantName = true;
      if (this.tenantNameKeyword) {
        // tenantNameKeywordが空でない場合のみフィルタリングを行う
        tenantName =
          element.tenantName
            .trim()
            .toLowerCase()
            .indexOf(this.tenantNameKeyword.toLowerCase()) > -1;
      }
      let companyName = true;
      if (this.companyNameKeyword) {
        // companyNameKeywordが空でない場合のみフィルタリングを行う
        companyName =
          element.companyName
            .trim()
            .toLowerCase()
            .indexOf(this.companyNameKeyword.toLowerCase()) > -1;
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return tenantId && tenantName && companyName;
    };
    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantList'] !== undefined) {
      const tenantList: TenantViewModel[] = changes['tenantList'].currentValue;
      this.dataSource.data = tenantList;
    }
  }

  /**
   * テナント詳細表示ダイアログを表示する
   * @param tenantId テナントId
   */
  public async openTenantDetailDialog(tenantId: string) {
    this.tenantService.find(tenantId).subscribe({
      next: (tenantDetail) => {
        const options: TenantDetailDialogViewModel = {
          tenantId: tenantDetail.tenantId,
          tenantName: tenantDetail.tenantName,
          description: tenantDetail.description,
          companyName: tenantDetail.companyName,
          companyLogoPath: tenantDetail.companyLogoPath,
          adminCount: tenantDetail.adminCount,
          userCount: tenantDetail.userCount,
          deviceCounts: tenantDetail.deviceCounts,
          assetTypeCount: tenantDetail.assetTypeCount,
          shipmentCount: tenantDetail.shipmentCount,
          shipmentLocationCount: tenantDetail.shipmentLocationCount,
          shipmentRouteCount: tenantDetail.shipmentRouteCount,
        };

        firstValueFrom(
          this.dialog
            .open(TenantDetailDailogComponent, {
              width: '700px',
              data: options,
            })
            .afterClosed(),
        );
      },
    });
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * テナントIDでのフィルタリングをクリアする。
   */
  public clearTenantIdFilter(): void {
    // フィルタ条件をクリア
    this.tenantIdKeyword = '';

    this.applyFilter();
  }

  /**
   * テナント名でのフィルタリングをクリアする。
   */
  public clearTenantNameFilter(): void {
    // フィルタ条件をクリア
    this.tenantNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 会社名でのフィルタリングをクリアする。
   */
  public clearCompanyNameFilter(): void {
    // フィルタ条件をクリア
    this.companyNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.tenantIdKeyword = filters.tenantId || '';
    this.tenantNameKeyword = filters.tenantName || '';
    this.companyNameKeyword = filters.companyName || '';
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: TenantListFilter = {
      tenantId: this.tenantIdKeyword,
      tenantName: this.tenantNameKeyword,
      companyName: this.companyNameKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
